import { flowRight, isString, compact } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { EXPERIMENT_RCV_GIPHY_PLUGIN, EXPERIMENT_USE_NEXT_RCE } from '@wix/communities-blog-experiments';
import withExperiment from '../../../app/hoc/with-experiment';
import withRichContentFontClassName from '../../../app/hoc/with-rich-content-font-class-name';
import { getPostPageEnabled } from '../../../app/selectors/app-data-selectors';
import getRce, { MAX_PINTEREST_IMAGE_SIZE, HTML_IFRAME_SRC } from '../../services/viewer';
import { openUrl } from '../../../app/actions/one-app';
import { navigateToBlogSection } from '../../../app/actions/navigate-to-blog-section';
import withDeviceType from '../../../app/hoc/with-device-type';
import { withEmotion } from '../../../app/hoc/with-emotion';
import {
  getAppSettings,
  getPostPageFontSizeMobile,
  getPostHeaderTwoFontSizeMobile,
  getPostHeaderThreeFontSizeMobile,
  getPostQuotesFontSizeMobile,
} from '../../../app/selectors/app-settings-selectors';
import { getCurrentLocationOuterUrl } from '../../../app/selectors/location-history-selectors';
import {
  isOneApp,
  isOneAppLinksSupported,
} from '@wix/communities-blog-client-common/dist/src/selectors/search-params-selectors';
import { isSeo } from '../../../app/selectors/view-mode-selectors';
import { push } from '../../../app/services/router-utils';
import { getImageUrl } from '../../../app/services/image-utils';
import { getVideoUrl } from '../../../app/services/video-utils';
import { isInEditor } from '../../../app/services/view-mode';
import { isPostPageBundle } from '../../../app/services/section-route-utils';
import { createHashtagHref, createHashtagPath } from '../../../app/services/search-page-url';
import { getTextColorSchema, styleSelectionPredicate, viewerCustomStyleFn } from '../../services/text-color-utils';
import Wix from '../../../app/services/wix-sdk-polyfill';
import getTheme from '../../services/theme';
import styles from './rich-content-viewer.scss';

const anchorTarget = '_top';

class RichContentViewer extends Component {
  state = { isViewerDisabled: false };

  constructor(props) {
    super(props);
    this.initViewerProps();
  }

  enableViewer = () => this.setState({ isViewerDisabled: false });
  disableViewer = () => this.setState({ isViewerDisabled: true });

  componentDidMount() {
    Wix.addEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.enableViewer);
    Wix.addEventListener(Wix.Events.PAGE_NAVIGATION_OUT, this.disableViewer);
  }

  componentWillUnmount() {
    Wix.removeEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.enableViewer);
    Wix.removeEventListener(Wix.Events.PAGE_NAVIGATION_OUT, this.disableViewer);
  }

  componentDidUpdate() {
    if (isInEditor()) {
      this.initTheme();
    }
  }

  getImagePluginConfig() {
    return {
      imageProps: src => ({
        'data-pin-url': this.props.pageUrl,
        'data-pin-media': isString(src) ? src : getImageUrl(src, MAX_PINTEREST_IMAGE_SIZE, MAX_PINTEREST_IMAGE_SIZE),
      }),
    };
  }

  getVideoPluginConfig = () => ({ getVideoUrl: src => getVideoUrl(src.pathname) });

  onHashTagClick = event => {
    event.preventDefault();
    event.stopPropagation();
    const pathname = createHashtagPath(event.target.innerText);

    if (this.props.isPostPageEnabled) {
      this.props.navigateToBlogSection(pathname);
    } else {
      this.props.push(pathname);
    }
  };

  initViewerProps() {
    const { enableLinks, isOneApp, appSettings, isGiphyPluginEnabled, useNextRCE } = this.props;
    this.rce = getRce(useNextRCE);
    const {
      GIPHY_TYPE,
      GiphyViewer,
      videoTypeMapper,
      imageTypeMapper,
      galleryTypeMapper,
      dividerTypeMapper,
      htmlTypeMapper,
      linkTypeMapper,
      TEXT_COLOR_TYPE,
      HTML_TYPE,
      IMAGE_TYPE,
      VIDEO_TYPE,
      GALLERY_TYPE,
      LINK_TYPE,
    } = this.rce;

    // https://github.com/wix-incubator/rich-content/issues/322
    const giphyTypeMapper = () => ({
      [GIPHY_TYPE]: { component: GiphyViewer },
    });

    this.typeMappers = compact([
      videoTypeMapper,
      imageTypeMapper,
      galleryTypeMapper,
      dividerTypeMapper,
      htmlTypeMapper,
      enableLinks && linkTypeMapper,
      isGiphyPluginEnabled && giphyTypeMapper,
    ]);

    if (enableLinks && isOneApp) {
      this.oneAppLinkConfig = {
        onClick: (event, url) => {
          event.preventDefault();
          this.props.openUrl({ url });
        },
      };
    }

    this.helpers = {};
    this.initTheme();

    const colorScheme = getTextColorSchema(appSettings);

    this.config = {
      [TEXT_COLOR_TYPE]: {
        customStyleFn: viewerCustomStyleFn(colorScheme),
        styleSelectionPredicate: styleSelectionPredicate(colorScheme),
      },
      [HTML_TYPE]: {
        htmlIframeSrc: HTML_IFRAME_SRC,
      },
      [IMAGE_TYPE]: this.getImagePluginConfig(),
      [VIDEO_TYPE]: this.getVideoPluginConfig(),
      [GALLERY_TYPE]: {
        imageProps: () => ({ 'data-pin-url': this.props.pageUrl }),
      },
      [LINK_TYPE]: this.oneAppLinkConfig,
    };

    this.initDecorators();
    this.initInlineStyleMappers(this.config);
  }

  initTheme() {
    const isPostPage = isPostPageBundle();

    const {
      appSettings,
      richContentFontClassName,
      useDescriptionFontInEditor,
      isPostPageEnabled,
      isCompact,
      isSeo,
      isMobile,
      emotion,
    } = this.props;

    this.theme = getTheme({
      appSettings,
      readOnly: true,
      isCompact,
      isViewer: true,
      isSeo,
      isMobile,
      isPostPageEnabled,
      richContentFontClassName,
      useDescriptionFontInEditor: useDescriptionFontInEditor && isPostPageEnabled,
      isPostPage,
      css: emotion.css,
    });
  }

  initDecorators() {
    const { HashtagDecorator, HEADERS_MARKDOWN_TYPE, createHeadersMarkdownDecorator } = this.rce;
    this.decorators = compact([
      new HashtagDecorator({
        theme: this.theme,
        onClick: this.onHashTagClick,
        createHref: createHashtagHref,
      }),
      createHeadersMarkdownDecorator({
        [HEADERS_MARKDOWN_TYPE]: {
          hideMarkdown: true,
        },
      }),
    ]);
  }

  initInlineStyleMappers(config) {
    this.inlineStyleMappers = [this.rce.textColorInlineStyleMapper(config, this.props.initialRawState)];
  }

  render() {
    const { initialRawState, isMobile, viewerKey, relValue } = this.props;
    const { advancedPostOpts, blogSharedValues } = this.theme;

    if (isInEditor()) {
      this.initTheme();
    }

    const Viewer = this.rce.RichContentViewer;

    return (
      <div className={styles.container}>
        <Viewer
          key={`${advancedPostOpts}${blogSharedValues.colors.linkHashtagColor}${viewerKey}`}
          typeMappers={this.typeMappers}
          ref={ref => (this.editor = ref)}
          {...this.props}
          initialState={initialRawState}
          helpers={this.helpers}
          decorators={this.decorators}
          theme={this.theme}
          platform={isMobile ? 'mobile' : 'desktop'}
          anchorTarget={anchorTarget}
          relValue={relValue}
          config={this.config}
          inlineStyleMappers={this.inlineStyleMappers}
          disabled={this.state.isViewerDisabled}
        />
      </div>
    );
  }
}

RichContentViewer.propTypes = {
  initialRawState: PropTypes.object,
  isCompact: PropTypes.bool,
  appSettings: PropTypes.object.isRequired,
  push: PropTypes.func.isRequired,
  navigateToBlogSection: PropTypes.func.isRequired,
  openUrl: PropTypes.func.isRequired,
  pageUrl: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isOneApp: PropTypes.bool.isRequired,
  enableLinks: PropTypes.bool.isRequired,
  isPostPageEnabled: PropTypes.bool.isRequired,
  richContentFontClassName: PropTypes.string.isRequired,
  useDescriptionFontInEditor: PropTypes.bool,
  useNextRCE: PropTypes.bool,
  isSeo: PropTypes.bool,
  isGiphyPluginEnabled: PropTypes.bool.isRequired,
  viewerKey: PropTypes.string,
  emotion: PropTypes.object.isRequired,
  relValue: PropTypes.string,
};

RichContentViewer.defaultProps = {
  isCompact: false,
  useDescriptionFontInEditor: true,
  relValue: 'noreferrer',
};

const computeViewerKey = state =>
  '' +
  getPostPageFontSizeMobile(state) +
  getPostHeaderTwoFontSizeMobile(state) +
  getPostHeaderThreeFontSizeMobile(state) +
  getPostQuotesFontSizeMobile(state);

const mapStateToProps = state => ({
  viewerKey: computeViewerKey(state),
  appSettings: getAppSettings(state),
  pageUrl: getCurrentLocationOuterUrl(state),
  isOneApp: isOneApp(state),
  enableLinks: !isOneApp(state) || isOneAppLinksSupported(state),
  isPostPageEnabled: getPostPageEnabled(state),
  isSeo: isSeo(state),
});

const mapDispatchToProps = {
  navigateToBlogSection,
  push,
  openUrl,
};

export default flowRight(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withDeviceType,
  withRichContentFontClassName,
  withExperiment({
    isGiphyPluginEnabled: EXPERIMENT_RCV_GIPHY_PLUGIN,
    useNextRCE: EXPERIMENT_USE_NEXT_RCE,
  }),
  withEmotion,
)(RichContentViewer);
